import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Minus, Plus, PackageCheck } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { prepareOrderLineItem } from '../../../../../features/orderDetails/orderDetailsThunks';
import { selectCanPickOrderLines } from '../../../../../features/auth/authSelectors';
import { selectIsRunning } from '../../../../../features/feedback/feedbackSlice';

const countsOf = (products) =>
  products.reduce((acc, line) => {
    acc[line.id] = line.prepared_quantity || 0;
    return acc;
  }, {});

/**
 * The counter and the button of one line, locked while that line's count is on
 * its way. Read per line from the store: one id for the whole card moved the
 * spinner to the last line clicked and freed a line that was still saving.
 */
const PickingControls = ({ line, quantity, isPrepared, onStep, onPrepare }) => {
  const isSubmitting = useSelector(selectIsRunning(prepareOrderLineItem, line.id));

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          icon={Minus}
          iconOnly
          pill
          onClick={() => onStep(line.id, -1, line.quantity)}
          disabled={quantity === 0 || isPrepared || isSubmitting}
          aria-label="Quitar una unidad"
        />
        <span
          data-testid="picking-quantity"
          className="w-8 text-center font-bold text-lg text-ninja-dark dark:text-white"
        >
          {quantity}
        </span>
        <Button
          variant="ghost"
          size="sm"
          icon={Plus}
          iconOnly
          pill
          onClick={() => onStep(line.id, 1, line.quantity)}
          disabled={quantity >= line.quantity || isPrepared || isSubmitting}
          aria-label="Agregar una unidad"
        />
      </div>

      <Button
        variant={isPrepared ? 'outline' : 'primary'}
        size="sm"
        pill
        onClick={() => onPrepare(line.id)}
        disabled={quantity === 0 || isPrepared}
        loading={isSubmitting}
        loadingLabel="Preparando..."
        aria-busy={isSubmitting}
        className="min-w-[110px]"
      >
        {isPrepared ? 'Preparado' : 'Preparar'}
      </Button>
    </div>
  );
};

/**
 * Product picking.
 *
 * One row per order line, with a counter the operator moves as units are found
 * on the shelf and a button that registers them. Lines already fully prepared
 * are locked so a second pass cannot double-count them.
 *
 * Either fulfillment addon buys the counters. Without them the lines stay on
 * screen as a read-only list, which is what the legacy shows: what the plan
 * withholds is the picking, not knowing what the order carries.
 *
 * @param {object} props
 * @param {object} props.order
 * @param {(message: string) => void} props.onError
 */
const OrderProductsCard = ({ order, onError }) => {
  const dispatch = useDispatch();
  const canPick = useSelector(selectCanPickOrderLines);
  const products = useMemo(() => order.products || [], [order.products]);
  const [quantities, setQuantities] = useState({});

  // The server count of each line as last copied into the counters. A refetch
  // moves a counter to the server's count only if it was not touched since:
  // registering one line refetches the order, and that used to send every
  // other counted line back to zero. A line just registered takes the real
  // count because `handlePrepare` records what it sent.
  const synced = useRef({});

  useEffect(() => {
    const previous = synced.current;
    synced.current = countsOf(products);
    setQuantities((current) =>
      products.reduce((acc, line) => {
        const server = line.prepared_quantity || 0;
        const draft = current[line.id];
        acc[line.id] = draft === undefined || draft === previous[line.id] ? server : draft;
        return acc;
      }, {})
    );
  }, [products]);

  if (products.length === 0) return null;

  const step = (lineId, delta, max) => {
    setQuantities((prev) => {
      const next = (prev[lineId] || 0) + delta;
      if (next < 0 || next > max) return prev;
      return { ...prev, [lineId]: next };
    });
  };

  const handlePrepare = async (lineId) => {
    const quantity = quantities[lineId] || 0;
    if (quantity === 0) return;

    const lastSynced = synced.current[lineId];
    synced.current[lineId] = quantity;
    try {
      await dispatch(prepareOrderLineItem({ orderId: order.id, orderProductId: lineId, quantity })).unwrap();
    } catch (error) {
      // Not registered: the count stays the operator's, not the server's.
      synced.current[lineId] = lastSynced;
      onError(error);
    }
  };

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border">
      <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-6 flex items-center gap-2">
        <PackageCheck className="text-ninja-green" size={20} />
        {canPick ? 'Preparación de Productos' : 'Productos'}
      </h3>

      <div className="divide-y divide-border/60">
        {products.map((line) => {
          const quantity = quantities[line.id] || 0;
          const isPrepared =
            line.status === 'prepared' || line.prepared_quantity === line.quantity;

          return (
            <div
              key={line.id}
              className={`flex flex-col sm:flex-row sm:items-center gap-4 py-4 ${isPrepared ? 'opacity-60' : ''}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ninja-dark dark:text-white truncate">
                  {line.product?.name || 'Producto sin nombre'}
                </p>
                <p className="text-sm text-text-secondary">
                  SKU: {line.product?.sku || '-'} &bull; Cantidad: {line.quantity} &bull; Peso: {line.product?.weight ?? '-'}
                </p>
              </div>

              {canPick && (
                <PickingControls
                  line={line}
                  quantity={quantity}
                  isPrepared={isPrepared}
                  onStep={step}
                  onPrepare={handlePrepare}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderProductsCard;
